import type { CheckType } from "lib/types/workflowConditions"

// ──────────────────────────────────────────────────────────────────────────
// Field-shape primitives the editor can render
// ──────────────────────────────────────────────────────────────────────────

/** Field kinds, each rendered by a dedicated sub-editor:
 *  - comparisonString — comparison + free-text values list
 *  - comparisonStringEnum — comparison + dropdown values list (enum-backed)
 *  - comparisonInteger — comparison + integer values list
 *  - arrayCount — wraps a ComparisonInteger as the array length
 *  - keyValues — list of optional key / value ComparisonStrings
 */
export type FieldKind =
  | "comparisonString"
  | "comparisonStringEnum"
  | "comparisonInteger"
  | "arrayCount"
  | "keyValues"

/** Identifies a backend-known enum the UI may render as a dropdown.
 *  Only `paymentProvider` is wired up today; the others fall back to
 *  free-text inputs. */
export type EnumSource =
  | "paymentProvider"
  | "shippingProvider"
  | "promotionProvider"
  | "voucherProvider"
  | "giftCardProvider"
  | "giftCardProductProvider"
  | "bonusProvider"
  | "taxProvider"
  | "externalPriceProvider"

export interface FieldDescriptor {
  /** Dot-notation path inside the check `fields` object — e.g.
   *  `name`, `billingAddress.email`, `customAttributes`. */
  path: string
  label: string
  kind: FieldKind
  /** Hint for the empty-input placeholder. */
  placeholder?: string
  /** For `comparisonStringEnum`: the backend enum to populate options from. */
  enumSource?: EnumSource
  /** Optional optgroup label for the "Add field" dropdown. Used by ADDRESSES
   *  to separate billing vs shipping. Fields without a group are listed
   *  ungrouped at the top of the dropdown. */
  group?: string
}

// ──────────────────────────────────────────────────────────────────────────
// Top-level shape of a condition check
// ──────────────────────────────────────────────────────────────────────────

/** Shape categories. The editor branches on `shape` to choose which sub-form
 *  to render for a given check type.
 *  - `compound` is handled by the group editor, not the leaf editor.
 *  - `field-bag` and `address-bag` allow the user to add/remove sub-fields.
 *  - `array-count`, `single-datetime`, `endpoint` each have one canonical
 *    sub-form. */
export type CheckShape =
  | "compound"
  | "field-bag"
  | "address-bag"
  | "array-count"
  | "single-datetime"
  | "endpoint"

export interface CheckDescriptor {
  type: CheckType
  label: string
  /** Display group used by the type-picker dropdown's optgroups. */
  group:
    | "Compound"
    | "Provider"
    | "Order"
    | "Order line"
    | "Address"
    | "Discount"
    | "Custom"
  shape: CheckShape
  /** For `field-bag` and `address-bag`: the selectable sub-fields. */
  fields?: FieldDescriptor[]
}

// ──────────────────────────────────────────────────────────────────────────
// Reusable field sets
// ──────────────────────────────────────────────────────────────────────────

const ADDRESS_FIELDS: FieldDescriptor[] = [
  { path: "country", label: "Country", kind: "comparisonString", placeholder: "e.g. SE" },
  { path: "pcc", label: "PCC", kind: "comparisonString" },
  { path: "telephoneNumber", label: "Telephone number", kind: "comparisonString" },
  { path: "stateOrProvince", label: "State / province", kind: "comparisonString" },
  { path: "streetAddress", label: "Street address", kind: "comparisonString" },
  { path: "streetAddress2", label: "Street address 2", kind: "comparisonString" },
  { path: "city", label: "City", kind: "comparisonString" },
  { path: "givenName", label: "Given name", kind: "comparisonString" },
  { path: "familyName", label: "Family name", kind: "comparisonString" },
  { path: "postalCode", label: "Postal code", kind: "comparisonString" },
  { path: "email", label: "Email", kind: "comparisonString", placeholder: "alice@example.com" }
]

// ──────────────────────────────────────────────────────────────────────────
// Per-type descriptors
// ──────────────────────────────────────────────────────────────────────────

export const CHECK_DESCRIPTORS: Record<CheckType, CheckDescriptor> = {
  AND: { type: "AND", label: "AND group", group: "Compound", shape: "compound" },
  OR: { type: "OR", label: "OR group", group: "Compound", shape: "compound" },

  PAYMENT_PROVIDER: {
    type: "PAYMENT_PROVIDER",
    label: "Payment provider",
    group: "Provider",
    shape: "field-bag",
    fields: [
      { path: "id", label: "ID", kind: "comparisonString" },
      {
        path: "name",
        label: "Name",
        kind: "comparisonStringEnum",
        enumSource: "paymentProvider"
      },
      { path: "status", label: "Status", kind: "comparisonString" },
      { path: "method", label: "Method", kind: "comparisonString" }
    ]
  },
  SHIPPING_PROVIDER: {
    type: "SHIPPING_PROVIDER",
    label: "Shipping provider",
    group: "Provider",
    shape: "field-bag",
    fields: [
      { path: "id", label: "ID", kind: "comparisonString" },
      { path: "name", label: "Name", kind: "comparisonString" }
    ]
  },
  PROMOTION_PROVIDER: {
    type: "PROMOTION_PROVIDER",
    label: "Promotion provider",
    group: "Provider",
    shape: "field-bag",
    fields: [
      { path: "id", label: "ID", kind: "comparisonString" },
      { path: "name", label: "Name", kind: "comparisonString" }
    ]
  },
  VOUCHER_PROVIDER: {
    type: "VOUCHER_PROVIDER",
    label: "Voucher provider",
    group: "Provider",
    shape: "field-bag",
    fields: [
      { path: "id", label: "ID", kind: "comparisonString" },
      { path: "name", label: "Name", kind: "comparisonString" }
    ]
  },
  GIFT_CARD_PROVIDER: {
    type: "GIFT_CARD_PROVIDER",
    label: "Gift card provider",
    group: "Provider",
    shape: "field-bag",
    fields: [
      { path: "id", label: "ID", kind: "comparisonString" },
      { path: "name", label: "Name", kind: "comparisonString" },
      { path: "status", label: "Status", kind: "comparisonString" },
      { path: "giftCards", label: "Gift card count", kind: "arrayCount" }
    ]
  },
  GIFT_CARD_PRODUCT_PROVIDER: {
    type: "GIFT_CARD_PRODUCT_PROVIDER",
    label: "Gift card product provider",
    group: "Provider",
    shape: "field-bag",
    fields: [
      { path: "id", label: "ID", kind: "comparisonString" },
      { path: "name", label: "Name", kind: "comparisonString" },
      { path: "status", label: "Status", kind: "comparisonString" },
      { path: "giftCardProducts", label: "Gift card product count", kind: "arrayCount" }
    ]
  },
  BONUS_PROVIDER: {
    type: "BONUS_PROVIDER",
    label: "Bonus provider",
    group: "Provider",
    shape: "field-bag",
    fields: [
      { path: "id", label: "ID", kind: "comparisonString" },
      { path: "name", label: "Name", kind: "comparisonString" },
      { path: "status", label: "Status", kind: "comparisonString" },
      { path: "customerId", label: "Customer ID", kind: "comparisonString" },
      { path: "amount", label: "Amount", kind: "comparisonInteger" },
      { path: "points", label: "Points", kind: "comparisonInteger" }
    ]
  },
  TAX_PROVIDER: {
    type: "TAX_PROVIDER",
    label: "Tax provider",
    group: "Provider",
    shape: "field-bag",
    fields: [
      { path: "id", label: "ID", kind: "comparisonString" },
      { path: "name", label: "Name", kind: "comparisonString" }
    ]
  },
  EXTERNAL_PRICE_PROVIDER: {
    type: "EXTERNAL_PRICE_PROVIDER",
    label: "External price provider",
    group: "Provider",
    shape: "field-bag",
    fields: [
      { path: "id", label: "ID", kind: "comparisonString" },
      { path: "name", label: "Name", kind: "comparisonString" }
    ]
  },

  ORDER_LINE: {
    type: "ORDER_LINE",
    label: "Order line",
    group: "Order line",
    shape: "field-bag",
    fields: [
      {
        path: "productParentId",
        label: "Product parent ID",
        kind: "comparisonString",
        placeholder: "e.g. shirt-001"
      },
      {
        path: "productVariantId",
        label: "Product variant ID",
        kind: "comparisonString",
        placeholder: "e.g. shirt-001-blue-m"
      },
      // The campaign sub-bag is editable as 3 distinct paths under
      // `campaign.fields.<x>` — the leaf editor flattens them so the user
      // sees them alongside other order-line fields.
      {
        path: "campaign.fields.campaignId",
        label: "Campaign ID",
        kind: "comparisonString"
      },
      {
        path: "campaign.fields.name",
        label: "Campaign name",
        kind: "comparisonString"
      },
      {
        path: "campaign.fields.campaignGroupId",
        label: "Campaign group ID",
        kind: "comparisonString"
      },
      {
        path: "customAttributes",
        label: "Custom attributes",
        kind: "keyValues"
      },
      { path: "tags", label: "Tags", kind: "keyValues" },
      { path: "options", label: "Options", kind: "keyValues" }
    ]
  },

  ORDER_LINES: {
    type: "ORDER_LINES",
    label: "Order line count",
    group: "Order",
    shape: "array-count"
  },
  SHIPPING_FEES: {
    type: "SHIPPING_FEES",
    label: "Shipping fee count",
    group: "Order",
    shape: "array-count"
  },
  GIFTS: {
    type: "GIFTS",
    label: "Gift count",
    group: "Order",
    shape: "array-count"
  },

  ORDER_TOTALS: {
    type: "ORDER_TOTALS",
    label: "Order totals",
    group: "Order",
    shape: "field-bag",
    fields: [
      { path: "subTotal", label: "Sub total", kind: "comparisonInteger" },
      { path: "taxTotal", label: "Tax total", kind: "comparisonInteger" },
      { path: "discountTotal", label: "Discount total", kind: "comparisonInteger" },
      { path: "shippingTotal", label: "Shipping total", kind: "comparisonInteger" },
      { path: "bonusTotal", label: "Bonus total", kind: "comparisonInteger" },
      { path: "giftCardTotal", label: "Gift card total", kind: "comparisonInteger" },
      { path: "grandTotal", label: "Grand total", kind: "comparisonInteger" }
    ]
  },

  ORDER_DATE: {
    type: "ORDER_DATE",
    label: "Order date",
    group: "Order",
    shape: "single-datetime"
  },

  CUSTOM_HTTP: {
    type: "CUSTOM_HTTP",
    label: "Custom HTTP request",
    group: "Custom",
    shape: "endpoint"
  },

  ADDRESSES: {
    type: "ADDRESSES",
    label: "Addresses",
    group: "Address",
    shape: "address-bag",
    fields: [
      ...ADDRESS_FIELDS.map((f) => ({
        ...f,
        path: `billingAddress.${f.path}`,
        group: "Billing address"
      })),
      ...ADDRESS_FIELDS.map((f) => ({
        ...f,
        path: `shippingAddress.${f.path}`,
        group: "Shipping address"
      }))
    ]
  },

  DISCOUNT_CODE_RULE: {
    type: "DISCOUNT_CODE_RULE",
    label: "Discount code rule",
    group: "Discount",
    shape: "field-bag",
    fields: [
      { path: "id", label: "ID", kind: "comparisonString" },
      { path: "code", label: "Code", kind: "comparisonString" }
    ]
  }
}

/** All check types that the leaf editor can render — i.e. everything except
 *  the compound AND/OR shapes (those are handled by the group editor). */
export const LEAF_CHECK_TYPES: CheckType[] = (
  Object.keys(CHECK_DESCRIPTORS) as CheckType[]
).filter((t) => CHECK_DESCRIPTORS[t].shape !== "compound")

/** Type-picker grouping for the leaf editor's `<select>`. Order is the
 *  display order. */
export const CHECK_TYPE_GROUPS: {
  label: string
  options: { value: CheckType; label: string }[]
}[] = (() => {
  const groupOrder: CheckDescriptor["group"][] = [
    "Provider",
    "Order",
    "Order line",
    "Address",
    "Discount",
    "Custom"
  ]
  return groupOrder.map((group) => ({
    label: group,
    options: LEAF_CHECK_TYPES.filter((t) => CHECK_DESCRIPTORS[t].group === group).map(
      (t) => ({ value: t, label: CHECK_DESCRIPTORS[t].label })
    )
  }))
})()
